<template>
  <div id="loginView" class="utd-form login-view ds1-utd-js2">
    <div v-if="isSpinnerVisible" class="loading-screen">
      <utd-loading-screen>
        <template #loadingText>
          {{ $t('LOGIN.SSO_LOADING_MESSAGE') }}
        </template>
      </utd-loading-screen>
    </div>
    <div v-else class="login-container">
      <div v-if="isXAIMobile" class="login-logo-header">
        <img :src="expertAiLogo" class="expert-ai-logo" alt="Expert AI">
      </div>
      <div class="floating-labels-form">
        <utd-logo v-if="isNotDesktopView && !isXAIMobile" />
        <form id="loginForm"
              class="form-login jumpstart-form"
              name="loginForm"
              :class="{isActive: isActive}"
              @submit.prevent="">
        <fieldset>
            <div class="login-form-title-container">
            <h3 v-if="isDesktopView" class="login-form-title">{{ $t('LOGIN.LOG_IN') }}</h3>
            <a v-if="!isUccState && !isPriorityLogin"
                 id="need-help-signing-in"
                 href="#"
                 class="login-form-link"
                 @click="openSignInAdviceModal()">
                <i class="wkce-icon-info" />
                <span class="form-link-text"
                    aria-label="{{ $t('LOGIN.SIGN_IN_EXPLAINER') }}">{{ $t('LOGIN.HELP') }}</span>
            </a>
            </div>
            <div v-show="isUnPwPriority || !isPriorityLogin">
            <utd-notification-adapter :notification-mode="message.level"
                                      :notification-content="getErrorContent()"
                                      :notification-title="message.title" />
            <utd-text-input :is-error="formErrors.username.isError"
                            :error-message="formErrors.username.errorMessage">
                <template #header>
                  <div class="form-label-container">
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
                    <label class="form-row" for="userName">
                    <div class="form-label-header">
                      <span class="label-above">{{ $t('LOGIN.USER_NAME') }}</span>
                    </div>
                    </label>
                    <utd-checkbox v-if="!isOidcUser"
                                class="remember-username-checkbox"
                                :aria-label="$t('LOGIN.REMEMBER_USERNAME')"
                                :checked="isRememberUsername"
                                @checkbox-changed="checkboxChanged">
                    {{ $t('LOGIN.REMEMBER_USERNAME') }}
                    </utd-checkbox>
                  </div>
                </template>
                <input id="userName"
                       v-model.trim="form.userName"
                       type="text"
                       class="form-field jumpstart-input"
                       autocomplete="username"
                       autocapitalize="off"
                       autocorrect="off"
                       maxlength="50"
                       aria-required="true"
                       @input="resetForm()">
              </utd-text-input>
            

              <label v-if="showPasswordSection" for="password" class="form-row">
                <utd-text-input :is-error="formErrors.password.isError"
                                :error-message="formErrors.password.errorMessage">
                <template #header>
                  <div class="form-links form-label-container">
                      <span class="label-above">{{ $t('LOGIN.PASSWORD') }}</span>
                      <span role="button"
                            tabindex="0"
                            :aria-label="$t(showPassword ? 'HIDE' : 'SHOW')"
                            class="show-password-toggle right label-above wkce-icon-visible"
                            @keyup.enter="togglePasswordVisibility"
                            @click="togglePasswordVisibility">
                        {{ $t(showPassword ? 'HIDE' : 'SHOW') }}
                      </span>
                    </div>
                  </template>
                  <div class="form-input-icon">
                    <input id="password"
                           v-model="form.password"
                           autocomplete="current-password"
                           name="password"
                           class="form-field jumpstart-input"
                           maxlength="24"
                           aria-required="true"
                           :type="showPassword ? 'text' : 'password'"
                           @click="checkCase($event)"
                           @keydown="checkCase($event)"
                           @focus="checkCase($event)"
                           @blur="resetCaps()"
                           @input="resetPasswordFieldError()">
                    <span v-show="capsOn" class="wkce-icon-filled-shift input-icon" />
                  </div>
                </utd-text-input>
              </label>

              <div class="form-break form-row">
                <span class="form-control">
                  <button v-if="!showPasswordSection"
                          id="btnContinueLogin"
                          class="form-button login-button"
                          :class="{isActive: isActive}"
                          @click="processUsername">
                    <span v-show="form.isSubmitting" id="continueBtnPleaseWaitText">
                      {{ $t('PLEASE_WAIT') }}</span>
                    <span v-show="!form.isSubmitting" id="continueBtnContinueText">
                      {{ $t('LOGIN.CONTINUE') }}</span>
                  </button>
                  <button v-if="showPasswordSection"
                          id="btnLoginSubmit"
                          class="form-button login-button"
                          :class="{isActive: isActive}"
                          @click="login">
                    <span v-show="form.isSubmitting">{{ $t('PLEASE_WAIT') }}</span>
                    <span v-show="!form.isSubmitting">{{ $t('LOGIN.LOG_IN') }}</span>
                  </button>
                </span>
                </div>

              <span v-if="showPasswordSection"
                    id="forgot-username-password">
                <a href="#"
                   class="login-form-link"
                   @click="openForgotCredentials">
                  <i class="wk-icon wkce-icon-question-circle" />
                  <span class="form-link-text">{{ $t('LOGIN.SIGN_IN_HELP') }}</span>
                </a>
              </span>
              <span v-else
                    id="forgot-username">
                <a href="#"
                   class="login-form-link"
                   @click="openForgotCredentials">
                  <i class="wk-icon wkce-icon-question-circle" />
                  <span class="form-link-text">{{ $t('LOGIN.FORGOT_USERNAME') }}</span>
                </a>
              </span>
            </div>
            
            <div v-if="showSearchableInstitutionsSection">
              <div v-if="!isPriorityLogin"
                   role="separator"
                   class="options-separator institutions-separator"
                   aria-label="SSO sign-in options">or</div>
              <searchable-institutions-section class="searchable-institutions-section"
                                               :is-merging="!!isOidcUser"
                                               :is-priority-login="isPriorityLogin"
                                               @show-spinner="navigateToSso" />
            </div>
          </fieldset>
        </form>

        <div v-show="!isPriorityLogin && showCaptchaSection" id="captchaContainer">
          <div role="separator" class="options-separator">Continue without signing in</div>
          <div class="form-control">
            <button v-show="!continueAnonymous"
                    id="captchaContinueBtn"
                    :tabindex="showCaptchaSection ? '0': '1'"
                    class="form-button login-button"
                    @click="displayCaptcha">
              <span>
                {{ $t('LOGIN.CONTINUE') }}
              </span>
            </button>
            <div v-show="showCaptcha"
                 id="g-recaptcha"
                 class="g-recaptcha"
                 data-render="explicit"
                 data-action="validate" />
          </div>
          <p class="login-register-section-text">
            Click the button above in order to continue using UpToDate without signing in
          </p>
        </div>

        <div v-if="!isPriorityLogin && showRegisterSubscribeSection && !isSpinnerVisible">
          <div role="separator" class="options-separator">{{ registerSubscribeHeaderText }}</div>
          <login-register-subscribe>
            <template v-if="!isOidcUser" v-slot:registerSectionText>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p class="login-register-section-text" v-html="getLoginRegisterText" />
            </template>
          </login-register-subscribe>
        </div>

        <div v-if="isPriorityLogin" class="exit-priority-section">
          <div role="separator" class="options-separator exit-priority-separator">or</div>
          <a href="#" class="exit-priority-link" @click="exitPriorityLogin">More ways to sign in</a>
        </div>
      </div>
    </div>

    <login-footer />

    <user-name-info-tooltip v-if="!isXAIMobile" ref="userNameInfoTooltip" />
    <next-sign-in-steps-modal ref="nextStepsModal"
                              :institution-name="nextStepsInstName"
                              :sso-login-url="url"
                              :user-name="form.userName"
                              @navigate-to-sso="navigateToSso"
                              @open-learn-more-modal="openLearnMoreModal"
                              @open-two-accounts-modal="openTwoAccountsModal" />
    <sign-in-advice-modal ref="signInAdviceModal" />
    <next-steps-learn-more-modal ref="learnMoreDialog" :email="form.userName" />
    <two-accounts-modal ref="twoAccountsModal" />
    <forgot-password-modal ref="forgotPassword" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { getWindow, scrollToZero } from '_acaSrc/utility/DOM';
import {
    C_BROWSER,
    C_UI,
    C_ACCOUNT,
    C_MY_ACCOUNT,
    C_EVENTS,
    C_LOGIN_WORKFLOW,
    C_REG_MERGE,
    C_PRIORITY_LOGIN,
    byolErrorRegex,
    SSO_INSTITUTION_SELECTION
} from '_acaSrc/utility/constants';
import { getDecodeCookie } from '_acaSrc/utility/Cookies';
import { SET_IS_XAI_MOBILE } from '_acaSrc/store/device.store';
import {
    SET_FORM_SAVE_USER_NAME,
    SET_FORM_SUBMITTING,
    SET_FORM_USER_NAME,
    SET_MESSAGE_ERROR,
    SET_MESSAGE_TITLE,
    RESET_LOGIN_ERROR_MESSAGE,
    SET_ERROR_LEVEL,
    SET_SHOULD_REDIRECT_POST_SSO,
    SET_OIDC_MERGE_STATUS
} from '_acaSrc/store/login.store';
import UtdNotificationAdapter from '_acaSrc/components/shared/utd/UtdNotificationAdapter.vue';
import UtdCheckbox from '_acaSrc/components/shared/stdlib/UtdCheckbox.vue';
import SearchableInstitutionsSection from './SearchableInstitutionsSection.vue';
import LoginFooter from '_acaSrc/components/login/LoginFooter.vue';
import LoginRegisterSubscribe from '_acaSrc/components/login/LoginRegisterSubscribe.vue';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import NextSignInStepsModal from './NextSignInStepsModal.vue';
import SignInAdviceModal from './SignInAdviceModal.vue';
import PubSub from '_acaSrc/utility/PubSub';
import UserNameInfoTooltip from './UserNameInfoTooltip.vue';
import NextStepsLearnMoreModal from './NextStepsLearnMoreModal.vue';
import TwoAccountsModal from './TwoAccountsModal.vue';
import UtdLoadingScreen from '_acaSrc/components/shared/stdlib/UtdLoadingScreen.vue';
import ForgotPasswordModal from '_acaSrc/account/components/ForgotPasswordModal.vue';
import UtdLogo from '../header/utdLogo/UtdLogo.vue';
import { getQueryParamValues, setQueryParamValue } from '_acaSrc/utility/http';
import Logger from '_acaSrc/utility/Logger';
import { nullUndefinedOrEmpty } from '_acaSrc/utility/Validators';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdCache from '_acaSrc/utility/UtdCache';
import IndexManager from '_acaSrc/utility/IndexManager';
import expertAiLogo from '~_acaAssets/search/expert-ai-logo.svg';

export default {
    components: {
        UtdNotificationAdapter,
        UtdCheckbox,
        SearchableInstitutionsSection,
        LoginFooter,
        LoginRegisterSubscribe,
        NextSignInStepsModal,
        SignInAdviceModal,
        UserNameInfoTooltip,
        NextStepsLearnMoreModal,
        TwoAccountsModal,
        UtdLoadingScreen,
        ForgotPasswordModal,
        UtdLogo,
        UtdTextInput
    },
    data() {
        return {
            expertAiLogo,
            isActive: false,
            showPassword: false,
            isContinueLoginForUPTUser: false,
            isRememberUsername: getDecodeCookie('userName') !== '',
            isSSOPreferred: false,
            capsOn: false,
            loginErrorTranslation: '',
            url: '',
            doNotShowConfirmDialog: false,
            nextStepsInstName: '',
            shownNextStepsModal: false,
            isSpinnerVisible: false,
            spinnerTimeout: null,
            continueAnonymous: false,
            captchaStatus: '',
            formErrors: {
                username: { isError: false, errorMessage: 'Please enter your username' },
                password: { isError: false, errorMessage: 'Please enter your password' }
            },
            overridePriorityLogin: false
        };
    },
    computed: {
        ...mapGetters('auth', [ 'isOidcUser', 'isInstitutionalLoginVisible' ]),
        ...mapGetters('login', [
            'form',
            'message',
            'institutions',
            'loginSubscriberBox',
            'loginRegisterButton',
            'loginErrorMessage',
            'loginInfoMessage',
            'oidcMergeStatus'
        ]),
        ...mapGetters('device', [
            'browserType',
            'isDesktopView',
            'noNativeCapsLock',
            'isNotDesktopView',
            'isXAIMobile'
        ]),
        ...mapGetters('profile', [ 'userProfileVisitor' ]),
        ...mapGetters('feature', [ 'isSsoToSsoAutoMergeEnabled' ]),
        ...mapGetters('user', [ 'userLoggedIn' ]),
        ...mapGetters('app', [
            'myAccountSidebarRequestStatus',
            'isUccState',
            'router',
            'isProspectView',
            'publicCaptchaKey'
        ]),
        userNameForUserInSession() {
            return this.userProfileVisitor
                  && this.userProfileVisitor.userName;
        },
        getLoginRegisterText() {
            if (this.loginSubscriberBox) {
                return this.$t('LOGIN.OFFERS_A_NUMBER_OF_SUBSCRIPTIONS');
            }
            else if (this.loginRegisterButton) {
                return this.$t('LOGIN.REGISTER_FOR_AN_ACCOUNT');
            }
            return '';
        },
        showRegisterSubscribeSection() {
            return !this.isOidcUser
                && (this.loginSubscriberBox || this.loginRegisterButton)
                && !this.isXAIMobile;
        },
        showPasswordSection() {
            return this.isUccState
              || this.isContinueLoginForUPTUser
              || this.shownNextStepsModal
              || this.rememberUsernameAndPasswordLogin
              || this.hideSSOOptions
              || this.isUnPwPriority;
        },
        rememberUsernameAndPasswordLogin() {
            return this.isRememberUsername && !this.isSSOPreferred;
        },
        hasCaptchaQueryParam() {
            return this.captchaStatus === 'true';
        },
        showCaptcha() {
            return this.continueAnonymous && this.hasCaptchaQueryParam;
        },
        showCaptchaSection() {
            return this.hasCaptchaQueryParam
                && !this.isProspectView
                && !this.userLoggedIn
                && !this.isSpinnerVisible
                && !nullUndefinedOrEmpty(this.publicCaptchaKey);
        },
        hideByolSso() {
            return this.isOidcUser && !this.isSsoToSsoAutoMergeEnabled;
        },
        hideSSOOptions() {
            return this.userLoggedIn 
            && this.isFromLoginRegisterPage()
            || this.hideByolSso;
        },
        isLoginError() {
            return this.message.error === C_ACCOUNT.FORM_MESSAGING.WAYF_GENERIC_ERROR_ID;
        },
        isMergeRulesError() {
            return this.message.error === C_ACCOUNT.FORM_MESSAGING.
                MERGE_FAILURE_TYPE.MERGE_RULE_VIOLATION;
        },
        registerSubscribeHeaderText() {
            return this.isProspectView
                ? 'Are you new to UpToDate?'
                : 'Don\'t have an UpToDate account?';
        },
        ssoInstitutionSelected() {
            const ssoInstitution = getDecodeCookie(SSO_INSTITUTION_SELECTION);
            try {
                return JSON.parse(ssoInstitution);
            }
            catch {
                return null;
            }
        },
        isUnPwPriority() {
            const pr = new UtdCache().getPersistent(C_PRIORITY_LOGIN.LOGIN_PRIORITY_CACHE_KEY);
            return pr === C_PRIORITY_LOGIN.UN_PW_PRIORITY
                && !this.overridePriorityLogin
                && !this.showCaptchaSection;
        },
        isSsoPriority() {
            const pr = new UtdCache().getPersistent(C_PRIORITY_LOGIN.LOGIN_PRIORITY_CACHE_KEY);
            return pr === C_PRIORITY_LOGIN.SSO_PRIORITY
                && this.ssoInstitutionSelected
                && !this.overridePriorityLogin
                && !this.showCaptchaSection;
        },
        isPriorityLogin() {
            return this.isUnPwPriority || this.isSsoPriority;
        },
        showSearchableInstitutionsSection() {
            return this.isSsoPriority || !this.isPriorityLogin
                && !this.hideSSOOptions && this.isInstitutionalLoginVisible;
        }
    },
    mounted() {
        this[SET_FORM_SAVE_USER_NAME](this.isRememberUsername);
        this.activate();
        new PubSub().subscribe('wkutd.closeNextStepsDialog', this.closeNextStepsDialog);
    },
    beforeUnmount() {
        new PubSub().unsubscribe('wkutd.closeNextStepsDialog', this.closeNextStepsDialog);
        clearTimeout(this.spinnerTimeout);
        new IndexManager().bodyCss.clear('xAIMobile');
        this[SET_IS_XAI_MOBILE](false);
    },
    methods: {
        ...mapActions('app', [ 'delayExecuteUntilDataLoaded' ]),
        ...mapActions('login', [
            'logLoginEvent',
            'setErrorMessage',
            'setErrorTitle',
            'loginUser',
            'getLoginWorkflow'
        ]),
        ...mapMutations('login', [
            SET_MESSAGE_ERROR,
            SET_MESSAGE_TITLE,
            SET_FORM_SUBMITTING,
            SET_FORM_USER_NAME,
            SET_FORM_SAVE_USER_NAME,
            RESET_LOGIN_ERROR_MESSAGE,
            SET_ERROR_LEVEL,
            SET_SHOULD_REDIRECT_POST_SSO,
            SET_OIDC_MERGE_STATUS
        ]),
        ...mapMutations('device', [
            SET_IS_XAI_MOBILE
        ]),
        getErrorContent() {
            if (this.isLoginError) {
                return this.$t('LOGIN.WAYF_GENERIC_ERROR');
            }
            if (this.isMergeRulesError) {
                return this.$t('LOGIN.MERGE_RULES_ERROR');
            }
            return this.message.error;
        },
        validateCaptchaResult() {
            if (getWindow().grecaptcha && getWindow().grecaptcha.enterprise) {
                const token = getWindow().grecaptcha.enterprise.getResponse();
                try {
                    utdRest('captcha/validate', {
                        clientCaptchaResponse: token
                    });
                }
                catch {
                    return false;
                }
            }
        },
        displayCaptcha() {
            this.continueAnonymous = !this.continueAnonymous;
        },
        openForgotCredentials() {
            logUiClickEventHelper({
                uiElementName: 'wayfLogin_forgotCredentials'
            });
            this.$refs.forgotPassword.open();
        },
        closeTooltip() {
            this.$refs.userNameInfoTooltip.closeTooltip();
        },
        logUiClickEvent(element) {
            logUiClickEventHelper({
                uiElementName: element,
                targetUrl: '/account/reset-password'
            });
        },
        resetForm() {
            this.isContinueLoginForUPTUser = false;
            this.isRememberUsername = false;
            this.shownNextStepsModal = false;
            this.form.password = '';
            this.resetUsernameFieldError();
            this.resetPasswordFieldError();
        },
        resetUsernameFieldError() {
            this.formErrors.username.isError = false;
        },
        resetPasswordFieldError() {
            this.formErrors.password.isError = false;
        },
        resetLoginError() {
            this[SET_MESSAGE_ERROR]('');
            this[SET_MESSAGE_TITLE]('');
            this[SET_ERROR_LEVEL]('error');
            if (this.form.isSubmitting) {
                return;
            }
            // Hide keyboard
            const focus = getWindow().document.querySelector(':focus');
            if (focus) {
                focus.blur();
            }
        },
        validateUserName() {
            if (this.form.userName === '' || this.form.userName === undefined) {
                this.formErrors.username.isError = true;
                this.$el.querySelector('#userName').focus();
                return false;
            }
            this.resetUsernameFieldError();
            return true;
        },
        validatePassword() {
            if (this.form.password === '' || this.form.password === undefined) {
                this.formErrors.password.isError = true;
                this.$el.querySelector('#password').focus();
                return false;
            }
            this.resetPasswordFieldError();
            return true;
        },
        async processUsername() {
            if (this.form.isSubmitting || !this.validateUserName()) {
                return;
            }

            this.resetLoginError();
            await this.handleLoginWorkflow();
        },

        shouldForcePasswordFlow() {
            return this.isByolSsoAllowed;
        },

        forcePasswordFlow() {
            this.isContinueLoginForUPTUser = true;
            this.$nextTick(() => {
                const pwdElem = this.$el.querySelector('#password');
                pwdElem && pwdElem.focus();
            });
        },

        async handleLoginWorkflow() {
            this[SET_FORM_SUBMITTING](true);
            
            try {
                const response = await this.getLoginWorkflow({username: this.form.userName});
                this.handleResponseWorkFlow(response);
                this.processWorkflowResponse(response);
            } 
            finally {
                this[SET_FORM_SUBMITTING](false);
            }
        },

        processWorkflowResponse(response) {
            const workflow = response.workflow;
            this.isContinueLoginForUPTUser = workflow === C_LOGIN_WORKFLOW.PROMPT_USER_FOR_PASSWORD;
            this[SET_FORM_USER_NAME](this.form.userName.replace(/"/g, ''));

            this.$nextTick(() => {
                if (this.isContinueLoginForUPTUser) {
                    const pwdElem = this.$el.querySelector('#password');
                    pwdElem && pwdElem.focus();
                }

                if (workflow === C_LOGIN_WORKFLOW.SHOW_NEXT_STEPS_FOR_SIGN_IN) {
                    this.$refs.nextStepsModal.openModal();
                    this.shownNextStepsModal = true;
                }
            });
        },
        login() {
            if (this.form.isSubmitting) {
                return;
            }

            this.resetLoginError();
            if (!(this.validatePassword() && this.validateUserName())) {
                return;
            }

            this.isActive = true;
            this[SET_FORM_SUBMITTING](true);
            this.loginUser()
                .then(() => {
                    // nothing to do
                })
                .finally(() => {
                    this.isActive = false;
                    this[SET_FORM_SUBMITTING](false);

                    const notificationTitle = this.$el.querySelector('.utd-notification');
                    notificationTitle && notificationTitle.focus();

                    new UtdCache().setPersistent(
                        C_PRIORITY_LOGIN.LOGIN_PRIORITY_CACHE_KEY,
                        C_PRIORITY_LOGIN.UN_PW_PRIORITY, false);
                });
        },
        togglePasswordVisibility() {
            this.showPassword = !this.showPassword;
        },
        checkCase(event) {
            if (this.noNativeCapsLock) {
                if (event.getModifierState && event.getModifierState('CapsLock')) {
                    this.capsOn = true;
                }
                else {
                    this.capsOn = false;
                }
            }
        },
        resetCaps() {
            this.capsOn = false;
        },
        handleResponseWorkFlow(response) {
            if (response.workflow === C_LOGIN_WORKFLOW.REDIRECT_TO_SSO
                && response.url) {
                this.setSsoLoginUrl(response);
                this.navigateToSso(this.url);
            }
            if (response.workflow === C_LOGIN_WORKFLOW.SHOW_NEXT_STEPS_FOR_SIGN_IN 
                && response.name) {
                this.setSsoLoginUrl(response);
                this.nextStepsInstName = response.name;
            }
        },
        navigateToSso(ssoUrl) {
            new PubSub().publish(C_EVENTS.POSITION_FOOTER, {
                reset: true
            });
            scrollToZero();
            this.showLoadingSpinner();
            this[SET_SHOULD_REDIRECT_POST_SSO](true);

            // remember user logged in with sso
            new UtdCache().setPersistent(
                C_PRIORITY_LOGIN.LOGIN_PRIORITY_CACHE_KEY,
                C_PRIORITY_LOGIN.SSO_PRIORITY, false);

            if (this.isOidcUser) {
                // in the middle of an auto-merge, indicate this in the URL
                ssoUrl = setQueryParamValue(
                    ssoUrl, C_REG_MERGE.REG_MERGE_SOURCE, C_REG_MERGE.REGISTRATION_MERGE_TYPE
                );
            }
            setTimeout(() => {
                getWindow().location.href = ssoUrl;
            }, C_UI.DEFAULT_SPINNER_DELAY_MS);
        },
        setSsoLoginUrl(res) {
            this.url = `${res.url}?login_hint=${this.form.userName}`;
            if (this.form.saveUserName === undefined) {
                this[SET_FORM_SAVE_USER_NAME](false);
            }
            this.url += `&saveLogin=${this.form.saveUserName}`;
        },
        setupRecaptcha() {
            try {
                if (!this.publicCaptchaKey) {
                    return;
                }
                if (getWindow().grecaptcha && getWindow().grecaptcha.enterprise) {
                    setTimeout(() => {
                        getWindow().grecaptcha.enterprise.render('g-recaptcha', {
                            sitekey: this.publicCaptchaKey,
                            callback: this.validateCaptchaResult
                        });
                    }, C_UI.CAPTCHA_RENDER_DELAY_MS);
                }
            }
            catch(e) {
                Logger.warn(`Failed to initialize reCaptcha - ${e}`);
                this.captchaStatus = '';
            }
        },
        checkCaptchaStatus() {
            this.captchaStatus = getQueryParamValues(getWindow().location.href, 'captcha');
        },
        activate() {
            this.checkCaptchaStatus();
            const usernameData = getDecodeCookie('userName');
            let username = usernameData;
            try {
                const loginHint = JSON.parse(usernameData).login_hint;
                username = loginHint;
                this.isSSOPreferred = true;
            }
            catch (e) {
                this.isSSOPreferred = false;
            }

            if (username) {
                this[SET_FORM_USER_NAME](username.replace(/"/g, ''));
                this[SET_FORM_SAVE_USER_NAME](true);
            }

            // Check if we've received the initial data response
            // for the app being loaded the first time
            // eslint-disable-next-line complexity
            this.delayExecuteUntilDataLoaded(() => {
                this.logLoginEvent(this.showCaptchaSection);
                this.setupRecaptcha();
                if (this.browserType !== C_BROWSER.TYPE_SMALL_SCREEN) {
                    if ((this.rememberUsernameAndPasswordLogin)) {
                        this.$el.querySelector('#password').focus();
                    }
                    else {
                        this.$el.querySelector('#userName').focus();
                    }
                }
                if (this.loginErrorMessage?.message) {
                    this.setErrorMessage(this.loginErrorMessage?.message);
                    this.setErrorTitle(this.loginErrorMessage?.title);
                    this[SET_ERROR_LEVEL]('error');
                    this[RESET_LOGIN_ERROR_MESSAGE]();
                }
                else if (this.loginInfoMessage?.title) {
                    this.setErrorTitle(this.loginInfoMessage?.title);
                    this.setErrorMessage(this.loginInfoMessage?.error);
                    this[SET_ERROR_LEVEL](this.loginInfoMessage?.level);
                }
                else if (this.oidcMergeStatus) {
                    this.handleAutoMergeStatus();
                }
                if (!username
                      && this.myAccountSidebarRequestStatus
                        === C_MY_ACCOUNT.SIDEBAR_REQUEST.PENDING) {
                    if (this.userNameForUserInSession) {
                        this[SET_FORM_USER_NAME](this.userNameForUserInSession);
                    }
                }
                
                const xAIMobile = getQueryParamValues(getWindow().location.href, 'xAIMobile');
                if (xAIMobile && this.isNotDesktopView) {
                    this[SET_IS_XAI_MOBILE](true);
                    new IndexManager().bodyCss.setOrClear('xAIMobile', true);
                }
            });
        },
        checkboxChanged(value) {
            this[SET_FORM_SAVE_USER_NAME](value);
        },
        openLearnMoreModal({ keepOneAccount }) {
            this.$refs.nextStepsModal.closeModal({ showPasswordInput: false });
            this.$refs.learnMoreDialog.open({ keepOneAccount });
        },
        openTwoAccountsModal() {
            this.$refs.nextStepsModal.closeModal({ showPasswordInput: false });
            this.$refs.twoAccountsModal.openModal();
        },
        openSignInAdviceModal() {
            this.$refs.signInAdviceModal.open();
        },
        closeNextStepsDialog({ showPasswordInput }) {
            this.shownNextStepsModal = showPasswordInput;
            if (this.shownNextStepsModal) {
                this.$nextTick(() => {
                    this.$el.querySelector('#password').focus();
                });
            }
        },
        showLoadingSpinner() {
            this.isSpinnerVisible = true;
        },
        isFromLoginRegisterPage() {
            const searchParams = getQueryParamValues(getWindow().location.search, 'source');
            return searchParams === 'login-register-page';
        },
        handleAutoMergeStatus() {
            if (!this.oidcMergeStatus.match(byolErrorRegex)) {
                // Not a BYOL automerge error, ignore.
                return;
            }

            // otherwise, handle as an error
            this.setErrorTitle(C_ACCOUNT.FORM_MESSAGING.LOGIN_ERROR_TITLE);
            this.setErrorMessage(
                C_ACCOUNT.FORM_MESSAGING.MERGE_FAILURE_TYPE.MERGE_RULE_VIOLATION);
            this[SET_ERROR_LEVEL]('error');
            this[SET_OIDC_MERGE_STATUS]();
        },
        exitPriorityLogin() {
            this.form.password = '';
            this.overridePriorityLogin = true;
        }
    }
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@import (reference) '~_acaAssets/utd-button/utd-button';

#loginView .wk-logo {
  .ds1-pb-2();
  .ds1-mt-1();
}

html:has(body.xAIMobile.isTablet),
body.xAIMobile,
body.xAIMobile #appContainer,
body.xAIMobile #utd-main {
    height: 100%;
}

#appContainer:has(> #utd-main > #loginView),
#utd-main:has(> #loginView) {
    background-color: @WKCE-GRAY-TINT6;

    .xAIMobile & {
      background-color: @DS1-UTD-PRIMARY-BG-COLOR;
      height: 100%;
    }
}

#loginView {
    .xAIMobile & {
      min-height: 100%;
      display: flex;
      flex-direction: column;
    }
}

@media only screen and (min-width: 768px) {
  .isDesktop #appContainer:has(> #utd-main > #loginView),
  .isDesktop #utd-main:has(> #loginView) {
    background-color: @WKCE-WHITE;
  }
}

</style>

<style lang="less" scoped>
@import '~_acaAssets/wkce/colors/wkce-app-styles';

@TEXT-COLOR: @WKCE-GRAY-SHADE1;

.options-separator {
  .wk-options-separator();
  font-weight: 600;

  &.institutions-separator,
  &.exit-priority-separator {
    .ds1-mv-3();
  }
}

.login-register-section-text {
  text-align: left;
  .ds1-utd-size-2(); /* stylelint-disable-line order/order */
  .ds1-mt-3();
  color: @WKCE-GRAY;

  :deep(strong) {
    font-weight: 600;
  }

  :deep(ul) {
    .ds1-pl-3();
  }
}

.forgot-credentials-icon {
  color: @WKCE-BLUE-SHADE1;
}

.forgot-credentials-link {
  margin-right: 2px;
}

.utd-form {
  &.login-view {
    input[type=password]::-ms-reveal { /* stylelint-disable-line selector-no-vendor-prefix */
      display: none;
    }
  }

  .jumpstart-form .show-password-toggle.right {
    color: @DS1-UTD-JS2-LINK-BASE;
    cursor: pointer;
    font-weight: normal;

    &::before {
      .ds1-mr-1();
      position: relative;
      top: 2px;
    }
  }

  .wk-icon-link {
    .ds1-mh-1();
    font-size: 16px;

    .wk-icon {
      height: 100%;
    }
  }

  .form-label-header {
    display: flex;
    color: @TEXT-COLOR;
  }

  .form-links {
    .ds1-pb-0();
  }

  .label-above {
    font-weight: 600;
  }

  .form-row {
    .ds1-flex-column-wrap();
  }

  .form-break {
    .ds1-pt-2();
  }

  .login-form-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .form-label-container {
    .ds1-mt-3();
    .ds1-mb-1();
    .ds1-pa-0();
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .remember-username-checkbox {
    white-space: nowrap;
    color: @DS1-UTD-TITLE-TEXT-COLOR;
  }

  #forgot-username,
  #forgot-username-password {
    .ds1-mt-3();
    display: flex;
    justify-content: flex-end;
    flex: 1;
    padding-top: 6px;
  }

  .form-link-text {
    .ds1-mh-compact();
    .ds1-utd-size-2();
  }

  .wkce-icon-question-circle {
    font-size: 15px;
  }

  .wk-field {
    display: flex;
    flex-direction: column;

    &.wk-field-errored {
      :deep(input.form-field) {
        border-color: @DS1-UTD-ERROR;
      }

      :deep(.wk-field-error) {
        color: @DS1-UTD-ERROR;
        text-align: left;
      }
    }

  }
}

.login-button:disabled {
  opacity: 0.5;
  pointer-events: none;
  color: @DS1-UTD-LINK-COLOR;
}

.dialog-label {
  .ds1-mb-2();
  .ds1-utd-weight-bold();
}

.loading-screen {
  min-height: 50vh;
}

.exit-priority-section {
  display: flex;
  flex-direction: column;

  .exit-priority-link {
    font-size: 14px;
    text-align: left;
  }
}

@media only screen and (min-width: 768px) {
  .isDesktop .utd-form.login-view {
    .floating-labels-form {
      .ds1-mt-6();
    }
  }
}

// Media query added for max-height to reduce the margins.
// Subscribe section need to be visible on small resolution.
@media only screen and (max-height: 860px) {
  .isDesktop .utd-form {
    &.login-view .floating-labels-form {
      .ds1-mt-3();

      &.login-register-section {
        .ds1-mt-2();
      }
    }

    .form-links.institution-section {
      .ds1-pt-0();
    }

    .login-form-title {
      .ds1-pb-0();
      font-size: 22px;
      font-weight: 700;
    }
  }
}

.xAIMobile {
    .utd-form {
        padding: 0;
        margin: 0;
        background-color: @WKCE-WHITE;
    }
    
    .login-container {
        flex: 1;
        margin: 0;
        padding-top: 56px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
    }

    .login-logo-header {
        background: @WKCE-WHITE;
        display: flex;
        height: 133px;
        padding: 0;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex: 0 0 auto;
    }

    .expert-ai-logo {
        .ds1-pa-2();
        width: 100%;
        height: 100%;
        object-fit: contain;
        box-sizing: border-box;
    }

    .floating-labels-form {
        min-height: 0;
        padding: 24px 16px;
        margin: 0;
        max-width: none;
        border-radius: 16px 16px 0 0;
        background-color: @WKCE-CREAM-TINT2;
        flex: 1 0 auto;
    }

    .form-login {
        .ds1-pt-3();
        max-width: 400px;
        margin: 0 auto;
    }

    .form-field {
         border-radius: 50px;
         max-width: none; 
         text-overflow: ellipsis;
         font-size: 16px;
         font-weight: 300;
    }

    .form-link-text {
        color: @WKCE-CREAM-SHADE2;
        font-size: 14px;
        font-weight: 400;
    }

    .form-button.login-button {
        border-radius: 50px;
        background-color: @WKCE-CREAM-SHADE1; 
    }

    .label-above {
        color: @WKCE-CREAM-SHADE2;
        font-size: 14px;
        font-weight: 600;
    }

    .login-form-link {
        color: @WKCE-CREAM-SHADE2;
        font-size: 14px;
        font-weight: 400;
    }

    .remember-username-checkbox {
        color: @WKCE-CREAM-SHADE2;
    }

    .show-password-toggle.right.label-above.wkce-icon-visible {
        color: @WKCE-CREAM-SHADE2;
    }

    .exit-priority-link {
        color: @WKCE-CREAM-SHADE1;
    }

    .form-footer {
        display: none;
    }
} 
</style> 